import React, { useEffect, useRef, useState } from "react";
import Map from "react-map-gl/maplibre";
import { Source, Layer } from "react-map-gl/maplibre";
import bbox from "@turf/bbox";
import { Box, Typography, CircularProgress } from "@mui/material";
import { GeoJSONFeature, GeoJSONGeometry } from "../../types";
import "maplibre-gl/dist/maplibre-gl.css";
import { DEFAULT_MAP_VIEW } from '../../utilities/mapView';


export interface PreviewMapProps {
  points: GeoJSONFeature[];
  areas: GeoJSONFeature[];
  autoZoom?: boolean;
  zoomPadding?: number;
  maxZoom?: number;
  initialViewState?: {
    longitude: number;
    latitude: number;
    zoom: number;
  };
  mapStyle?: 'satellite' | 'streets' | 'terrain';
  onClick?: (event: any) => void;
  showLabels?: boolean;
}

const MAP_STYLES = {
  satellite: process.env.REACT_APP_MAPTILER_KEY 
    ? `https://api.maptiler.com/maps/satellite/style.json?key=${process.env.REACT_APP_MAPTILER_KEY}`
    : 'https://demotiles.maplibre.org/style.json', // Fallback style
  streets: process.env.REACT_APP_MAPTILER_KEY 
    ? `https://api.maptiler.com/maps/streets/style.json?key=${process.env.REACT_APP_MAPTILER_KEY}`
    : 'https://demotiles.maplibre.org/style.json', // Fallback style
  terrain: process.env.REACT_APP_MAPTILER_KEY 
    ? `https://api.maptiler.com/maps/terrain/style.json?key=${process.env.REACT_APP_MAPTILER_KEY}`
    : 'https://demotiles.maplibre.org/style.json' // Fallback style
};

export const PreviewMap: React.FC<PreviewMapProps> = ({
  points = [],
  areas = [],
  autoZoom = true,
  zoomPadding = 50,
  maxZoom = 10,
  initialViewState = DEFAULT_MAP_VIEW,
  mapStyle = 'satellite',
  onClick,
  showLabels = true
}) => {
  const mapRef = useRef<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Log API key status for debugging if needed
  if (!process.env.REACT_APP_MAPTILER_KEY) {
    console.warn('[PreviewMap] REACT_APP_MAPTILER_KEY not found, using fallback map style');
  }

  // Helper function to check if a geometry has coordinates
  const hasCoordinates = (geometry: GeoJSONGeometry): boolean => {
    if (!geometry) return false;
    
    if (geometry.type === 'GeometryCollection') {
      return geometry.geometries.some(g => hasCoordinates(g));
    }
    
    return 'coordinates' in geometry;
  };

  useEffect(() => {
    if (autoZoom && mapRef.current && (areas.length > 0 || points.length > 0)) {
      const validFeatures = [...areas, ...points].filter(
        feature => feature?.geometry && hasCoordinates(feature.geometry)
      );

      if (validFeatures.length === 0) {
        console.warn('No valid features found for bounds calculation');
        return;
      }

      try {
        const bounds = bbox({
          type: "FeatureCollection",
          features: validFeatures,
        });

        mapRef.current.fitBounds(
          [
            [bounds[0], bounds[1]],
            [bounds[2], bounds[3]],
          ],
          {
            padding: zoomPadding,
            duration: 1000,
            maxZoom: maxZoom,
          }
        );
      } catch (error) {
        console.error('Error calculating bounds:', error);
      }
    }
  }, [points, areas, autoZoom, zoomPadding, maxZoom]);

  const handleMapLoad = () => {
    setIsLoading(false);
  };

  const handleMapError = (error: any) => {
    console.error('[PreviewMap] Map failed to load:', error);
    setHasError(true);
    setIsLoading(false);
  };

  return (
    <Box sx={{ 
      flex: 1, 
      borderRadius: 0, 
      overflow: "hidden", 
      border: "1px solid rgba(255, 255, 255, 0.1)",
      position: "relative",
      width: "100%",
      height: "100%",
      minHeight: "300px"
    }}>
      {/* Loading indicator */}
      {isLoading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0, 0, 0, 0.1)",
            zIndex: 1000,
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
              Loading map...
            </Typography>
          </Box>
        </Box>
      )}

      {/* Error state */}
      {hasError && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(240, 240, 240, 0.9)",
            zIndex: 1000,
          }}
        >
          <Box sx={{ textAlign: "center", p: 3 }}>
            <Typography variant="h6" color="error" gutterBottom>
              Map failed to load
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please check your internet connection or try again later.
            </Typography>
          </Box>
        </Box>
      )}

      <Map
        ref={mapRef}
        initialViewState={initialViewState}
        maxPitch={65}
        style={{ width: "100%", height: "100%" }}
        mapStyle={MAP_STYLES[mapStyle]}
        attributionControl={false}
        onClick={onClick}
        onLoad={handleMapLoad}
        onError={handleMapError}
      >
        <Source type="geojson" data={{ type: "FeatureCollection", features: areas }}>
          <Layer
            id="areas-fill"
            type="fill"
            filter={["in", ["geometry-type"], ["literal", ["Polygon", "MultiPolygon"]]]}
            paint={{
              "fill-color": "#ffff00",
              "fill-opacity": 0.1,
            }}
          />
          <Layer
            id="areas-stroke"
            type="line"
            filter={["in", ["geometry-type"], ["literal", ["Polygon", "MultiPolygon"]]]}
            paint={{
              "line-color": "#ffff00",
              "line-width": 2.25,
              "line-opacity": 0.8,
            }}
          />
          <Layer
            id="linestring-stroke"
            type="line"
            filter={["in", ["geometry-type"], ["literal", ["LineString", "MultiLineString"]]]}
            paint={{
              "line-color": "#ffff00",
              "line-width": 3,
              "line-opacity": 0.9,
            }}
          />
          {/*
            Point-family geometries can arrive in `areas` too: a MultiPoint is not a
            single [lng, lat], so it travels the same path as the other multi types.
            Without this layer it lands in the source and draws nothing.
          */}
          <Layer
            id="areas-points"
            type="circle"
            filter={["in", ["geometry-type"], ["literal", ["Point", "MultiPoint"]]]}
            paint={{
              "circle-radius": 8,
              "circle-color": "#B42222",
              "circle-opacity": 0.8,
              "circle-stroke-width": 1,
              "circle-stroke-color": "#FFFFFF",
            }}
          />
          {showLabels && (
            <Layer
              id="areas-labels"
              type="symbol"
              layout={{
                "text-field": ["get", "name"],
                "text-offset": [0, 0],
                "text-anchor": "center",
                "text-size": 16,
                "text-allow-overlap": true,
                "text-ignore-placement": true,
              }}
              paint={{
                "text-color": "#ffffff",
                "text-halo-color": "#000000",
                "text-halo-width": 2,
              }}
            />
          )}
        </Source>

        <Source type="geojson" data={{ type: "FeatureCollection", features: points }}>
          <Layer
            id="points-circles"
            type="circle"
            paint={{
              "circle-radius": 8,
              "circle-color": "#B42222",
              "circle-opacity": 0.8,
              "circle-stroke-width": 1,
              "circle-stroke-color": "#FFFFFF",
            }}
          />
          {showLabels && (
            <Layer
              id="points-labels"
              type="symbol"
              layout={{
                "text-field": ["get", "name"],
                "text-offset": [0, 1.5],
                "text-anchor": "top",
                "text-size": 14,
                "text-allow-overlap": true,
                "text-ignore-placement": true,
              }}
              paint={{
                "text-color": "#ffffff",
                "text-halo-color": "#000000",
                "text-halo-width": 2,
              }}
            />
          )}
        </Source>
      </Map>
    </Box>
  );
}; 
